import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { CheckCircle2, Receipt, RefreshCw, Undo2, Webhook, XCircle } from 'lucide-react'
import StatusBadge from '@/components/platform/StatusBadge'
import { usePayments, useRefundPayment, useWebhookStatus } from '@/lib/gateway'
import { useAlertContext } from '@/providers/AlertProvider'
import { RequireRole } from '@/providers/RequireRole'
import { UserRole } from '@/types/auth'

function money(amount?: number, currency?: string) {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount)
}

function ago(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 48) return `${hours} h ago`
  return `${Math.round(hours / 24)} days ago`
}

/** Stripe webhook health. A configured secret with a long silence is the thing to act on: check the
 * endpoint URL and signing secret in the Stripe dashboard (bad signatures are rejected before they are
 * stored, so they cannot be listed here). */
function WebhookStatusCard() {
  const { data, isLoading, isError, error } = useWebhookStatus()

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Webhook className="w-5 h-5 text-gray-500" />
        <h2 className="font-semibold text-gray-800 dark:text-gray-100">Stripe webhooks</h2>
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-500">Checking webhook status…</p>
      ) : isError || !data ? (
        <p className="text-sm text-red-600">Couldn&apos;t load webhook status: {(error as Error)?.message}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className={`inline-flex items-center gap-1.5 font-medium ${data.secretConfigured ? 'text-green-700 dark:text-green-400' : 'text-red-600'}`}>
              {data.secretConfigured ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {data.secretConfigured ? 'Signing secret configured' : 'Signing secret NOT configured — webhooks will be rejected'}
            </span>
            <span className="text-gray-600 dark:text-gray-300">Last event: <strong>{ago(data.lastEventAt)}</strong></span>
            <span className="text-gray-600 dark:text-gray-300">24 h: <strong>{data.last24hCount}</strong></span>
            <span className="text-gray-600 dark:text-gray-300">7 days: <strong>{data.last7dCount}</strong></span>
          </div>
          {data.byType.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.byType.map((t) => (
                <span key={t.type} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                  {t.type} · {t.count}
                </span>
              ))}
            </div>
          )}
          {data.secretConfigured && data.lastEventAt == null && (
            <p className="text-xs text-amber-600">No events received yet — send a test event from the Stripe dashboard (Developers → Webhooks) or the Stripe CLI.</p>
          )}
          {data.recent.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-600 dark:text-gray-300">Recent events ({data.recent.length})</summary>
              <table className="mt-2 min-w-full text-left text-xs">
                <thead className="text-gray-500">
                  <tr><th className="py-1 pr-4">Type</th><th className="py-1 pr-4">Event id</th><th className="py-1">Processed</th></tr>
                </thead>
                <tbody>
                  {data.recent.map((e) => (
                    <tr key={e.eventId} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-1 pr-4 text-gray-700 dark:text-gray-200">{e.type}</td>
                      <td className="py-1 pr-4 font-mono text-gray-500">{e.eventId}</td>
                      <td className="py-1 text-gray-500">{new Date(e.processedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
    </div>
  )
}

function PaymentsPage() {
  const [page, setPage] = useState(1)
  const { data: payments = [], isLoading, isError, error, isFetching, refetch } = usePayments(page)
  const refund = useRefundPayment()
  const { confirm, success, error: showError } = useAlertContext()

  const requestRefund = (id: string, amount: number) => {
    confirm({
      title: 'Refund payment',
      message: `Refund ${money(amount)}? This cannot be undone.`,
      type: 'warning',
      confirmText: 'Refund',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          await refund.mutateAsync({ id, amount, reason: 'Admin-initiated refund' })
          success('Refunded', 'The refund was submitted successfully')
        } catch (err) {
          showError('Refund failed', (err as Error)?.message || 'Failed to submit refund')
        }
      },
    })
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><Receipt className="w-6 h-6" /> Payments</h1>
          <p className="text-gray-600 dark:text-gray-400">Transaction history and refunds (Stripe-backed).</p>
        </div>
        <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /></button>
      </div>

      <WebhookStatusCard />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading payments…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load payments: {(error as Error)?.message}. Sign in required — this endpoint requires an authenticated session.</p>
        ) : payments.length === 0 ? (
          <p className="p-6 text-gray-500">No payments found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Amount</th>
                <th className="px-4 py-2">Method</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={p.id ?? i} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{p.orderId ?? '—'}</td>
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{money(p.amount, p.currency)}</td>
                  <td className="px-4 py-3">{p.method ?? '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 text-gray-500">{p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <RequireRole roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                        <button
                          disabled={!p.id || p.status === 'Refunded' || p.status === 'Failed'}
                          onClick={() => p.id && requestRefund(p.id, p.amount ?? 0)}
                          className="p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600 disabled:opacity-30"
                          title="Refund payment"
                        >
                          <Undo2 className="w-4 h-4" />
                        </button>
                      </RequireRole>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {payments.length > 0 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40">Previous</button>
          <span className="text-gray-500">Page {page}</span>
          <button onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">Next</button>
        </div>
      )}
    </div>
  )
}

export const Route = createFileRoute('/(admin)/payments/')({
  component: PaymentsPage,
})
